import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Party } from 'app/event/models/party';
import { PartyType } from 'app/event/models/party-type';
import { AdjustmentStore } from 'app/event/service/adjustment.store';
import { TaxDeterminationStore } from 'app/event/service/tax-determination.store';
import { Observable } from 'rxjs/Observable';
import { MenuStore } from '../../../menus/service/menu.store';
import { AuthenticationStore } from '../../../permission/services/authentication.store';
import { DateUtil } from '../../../shared/utils/date.util';
import { ConfigResponse } from '../../models/config/config-response';
import { OnyxEvent } from '../../models/event';
import { Payment } from '../../models/payment';
import { PaymentStatus } from '../../models/payment-status.enum';
import { EventStore } from '../../service/event.store';
import { GroupPayConfigStore } from '../../service/grouppay-config.store';
import { PaymentStore } from '../../service/payment.store';
import { EventUtils } from '../../utils/event-utils';
import { EnvironmentConfiguration } from 'app/environment/services/environment.configuration';
import { ResponseUtil } from '../../../shared/utils/response-util';
import { ReportType } from '../../models/report-type.enum';
import { ReportService } from '../../service/report.service';
import { AgencyLookupStore } from 'app/shared/lookup/agency-lookup/agency-lookup.store';
import { ConfirmationModalComponent } from 'app/shared/confirmation-modal/confirmation-modal.component';
import { FlashMessagesService } from 'app/shared/flash-messages/service/flash.messages.service';
import { LogService } from 'app/shared/log/log.service';
import { catchError, takeUntil } from 'rxjs/operators';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { Unsubscriber } from './../../../shared/helper/unsubscriber';
import { PaymentStatusUtils } from 'app/event/utils/payment-status-utils';
import { PaymentSummaryStore } from 'app/event/service/payment-summary/payment-summary.store';
import { ErrorHandlerService } from 'app/shared/utils/error-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { PaymentSummary } from 'app/event/models/payment-summary/payment-summary';

@Component({
  selector: 'pioneer-vendor-commission-card',
  templateUrl: './vendor-commission-card.component.html',
  styleUrls: ['./vendor-commission-card.component.css']
})
export class VendorCommissionCardComponent extends Unsubscriber implements OnInit {

  @Input() vendors: Party[];

  @Input() vendor: Party;

  @Input() config: ConfigResponse;

  @Input() isCardOpen = false;
  @Output() onToggleCard = new EventEmitter<void>();
  @ViewChild('cardWrapper') cardWrapper: ElementRef;

  isReadOnly = true;

  vendorForm: FormGroup;

  public imagesUrl = '';

  commissionForm: FormGroup;
  onyxEvent: OnyxEvent;
  finalPaymentStatusList: Payment[];
  prepayment: Payment;
  displayedPrepayment = false;
  isCollapsedAddAdjustment = true;
  private hasPrepayment: boolean;
  private isFinalPaymentAuthorized: boolean;
  canMakePrepayment = false;
  showPrepaymentHintText = true;
  ui = {
    showAddPayment: false,
    showPaymentRecord: false,
    showPaymentSummary: false,
    showDownloadDropdown: false
  };

  minAmountPrepayment: number;

  private readonly vendorFormValidators = {};

  applyOoiv = true;

  agencySubscriberUser = false;

  @ViewChild('confirmWithdrawPaymentRequestModal')
  confirmWithdrawPaymentRequestModal: ConfirmationModalComponent;

  readonly CONFIRM_ACTION = 'confirm';

  paymentSummary: PaymentSummary;

  constructor(private formBuilder: FormBuilder,
    private menuStore: MenuStore,
    private eventStore: EventStore,
    private paymentStore: PaymentStore,
    private authenticationStore: AuthenticationStore,
    private groupPayConfigStore: GroupPayConfigStore,
    private environment: EnvironmentConfiguration,
    private adjustmentStore: AdjustmentStore,
    private reportService: ReportService,
    private agencyLookupStore: AgencyLookupStore,
    private flashMessagesService: FlashMessagesService,
    private paymentSummaryStore: PaymentSummaryStore,
    private errorHandlerService: ErrorHandlerService,
    private translateService: TranslateService) {
      super();
  }

  ngOnInit() {

    if (!this.vendor || !this.vendor.eventPartyId) {
      const message = this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
        property: 'vendor.eventPartyId',
        className: 'VendorCommissionCardComponent'
      });
      this.errorHandlerService.throw('vendor.eventPartyId is null or undefined', message);
      return;
    }

    this.paymentSummaryStore.getPaymentSummary(this.vendor.eventPartyId)
      .pipe(
        takeUntil(this._destroyed$),
        catchError(err => {
          this.errorHandlerService.throw(err.message, this.translateService.instant('MESSAGES.ERROR.COULD_NOT_GET_PAYMENT_SUMMARY'));
          return Observable.empty();
        })
      )
      .subscribe((paymentSummary: PaymentSummary) => {
        this.paymentSummary = paymentSummary;
        const finalPaymentStatus = paymentSummary ? paymentSummary.paymentStatus : undefined;
        this.computePaymentStatus(finalPaymentStatus);
      });

    this.imagesUrl = this.environment.onyxGPImagesCommonDataUrl;

    this.addSubscription(this.eventStore.selectedEvent.subscribe((event: OnyxEvent) => {
      this.onyxEvent = new OnyxEvent(event);
    }));

    this.addSubscription(this.setupUi().subscribe());

    this.commissionForm = this.formBuilder.group({});
    if (this.onyxEvent && EventUtils.getEventName(this.onyxEvent, this.authenticationStore.isAgencyUser())) {
      this.menuStore.setPageHeader(EventUtils.getEventName(this.onyxEvent, this.authenticationStore.isAgencyUser()),
        this.onyxEvent.eventProperty.subscriber);
    } else {
      this.menuStore.setPageHeader('EVENT.EVENT_INFO_TITLE', false);
    }

    this.minAmountPrepayment = this.getMinAmount();

    this.checkPrepaymentPermissions();

    this.createForm();

    this.addSubscription(
      this.groupPayConfigStore.isOoivEnabled(
        this.onyxEvent.eventAgency.code, this.onyxEvent.eventAgency.countryCode, this.vendor.countryCode)
        .subscribe(ooivEnabled => {
          this.applyOoiv = ooivEnabled;
        }));

    if (!this.authenticationStore.isAgencyUser()) {
      this.agencySubscriberUser = false;
    } else {
      const iata = this.authenticationStore.getDefaultArcIata();
      if (iata) {
        this.addSubscription(this.agencyLookupStore.isSubscriberAgency(iata).subscribe(isSubscriber => {
          this.agencySubscriberUser = isSubscriber;
        }));
      }
    }

  }

  private createForm() {
    this.vendorForm = this.formBuilder.group({
      vendorName: this.vendor.name,
      vendorId: this.vendor.eventPartyId,
      vendorType: this.vendor.payorType,
      vendorCurrency: this.onyxEvent.currency,
      vendorStatus: 'Ready to Initiate',
      vendorCalculatedPayment: 150,
      commissionRoomPercentage: this.vendor.commissionRoomPercentage,
      commRetention: this.vendor.commRetention
    }, this.vendorFormValidators);
  }

  displayPrepaymentClick(): void {
    this.displayedPrepayment = true;
  }

  onAddPrepayment(): void {
    this.displayedPrepayment = false;
    this.hasPrepayment = true;
  }

  onCancelPrepayment(): void {
    this.displayedPrepayment = false;
  }

  isUserApprover(): boolean {
    return this.authenticationStore.hasPermission('AuthorizePayment');
  }

  displayAddPrepaymentButton(): boolean {
    return !this.displayedPrepayment && !this.hasPrepayment && !this.isEventStarted() && !this.isFinalPaymentAuthorized
      && !this.authenticationStore.isAgencyUser();
  }

  private isEventStarted(): boolean {
    const _startDate = EventUtils.getEventStartDate(this.onyxEvent, this.authenticationStore.isAgencyUser());
    const eventStartDate = DateUtil.parseDate(_startDate).getTime();
    return (eventStartDate < Date.now());
  }

  private getPrepayment(): Observable<Payment> {
    return this.paymentStore.getVendorPrepayment(this.vendor.eventPartyId).map((prepayment) => {
      return prepayment && prepayment.status ? prepayment : null;
    }).share();
  }

  private getMinAmount(): number {
    if (this.vendor.config != null && this.vendor.config.value != null) {
      return Number(this.vendor.config.value);
    }
    return null;
  }

  private checkPrepaymentPermissions(): void {
    const display = this.displayAddPrepaymentButton();
    const missingAgency = !EventUtils.doesEventHaveAgency(this.onyxEvent);
    this.showPrepaymentHintText = display && missingAgency;
    this.canMakePrepayment = this.isUserApprover() && EventUtils.doesEventHaveAgency(this.onyxEvent);
  }

  addAdjustmentClick(): void {
    this.isCollapsedAddAdjustment = false;
  }

  displayAddAdjustmentButton(): boolean {
    return !this.vendor.subscriber && this.ui.showAddPayment && this.isCollapsedAddAdjustment;
  }

  /**
   *  If it is an agency user and the event property is subscriber,
   *  the addAdjustment button must be disabled
   * @returns {boolean}
   */
  disableAddAdjustmentButton(): boolean {
    let disable: boolean;
    if (this.authenticationStore.isAgencyUser() && this.vendor.subscriber && this.vendor.payorType === 'PROPERTY') {
      disable = true;
    } else {
      disable = false;
    }
    return disable;
  }

  onAddAdjustment(): void {
    this.isCollapsedAddAdjustment = true;
  }

  onCancelAdjustment(): void {
    this.isCollapsedAddAdjustment = true;
  }

  confirmBeforeDelete($event: MouseEvent) {
    event.preventDefault();
  }

  confirmHandler(deleteConfirmed: boolean) {
    if (deleteConfirmed) {

    }
  }

  isVendorProperty(): boolean {
    return this.vendor.partyType === PartyType.PROPERTY && this.vendor.payorType === 'PROPERTY';
  }

  validEventReferenceNumber(): boolean {
    return this.vendor.eventReferenceNumber !== null && this.vendor.eventReferenceNumber.length > 0;
  }

  getPaymentsReport(fileType: string) {
    this.addSubscription(this.reportService.getPaymentsReport(this.onyxEvent.eventId, this.vendor.eventPartyId, fileType)
      .subscribe(blob => {
        let filename = ResponseUtil.getFileNameFromHttpResponse(blob);
        if (!filename) {
          filename = this.reportService.buildReportFileName(ReportType.PAYMENT_SUMMARY, this.onyxEvent, fileType);
        }
        saveAs(blob.body, filename);
      }));
  }

  canWithdrawPaymentRequest(): boolean {
    if (this.agencySubscriberUser && !this.vendor.subscriber && this.paymentSummary) {
      return this.paymentSummary.paymentStatus === PaymentStatus.REQUESTED
          || this.paymentSummary.paymentStatus === PaymentStatus.RESENT_REQUEST;
    }
    return false;
  }

  openWithDrawPaymentRequestModal() {
    if (this.canWithdrawPaymentRequest()) {
      this.confirmWithdrawPaymentRequestModal.open();
    }
  }

  confirmWithdrawPaymentRequestHandler(action: string | boolean) {
    if (action === this.CONFIRM_ACTION) {
      this.addSubscription(this.paymentStore.withdrawPaymentRequest(this.onyxEvent.eventId, this.vendor.eventPartyId)
        .take(1)
        .catch(() => {
          this.flashMessagesService.error('MESSAGES.ERROR.WITHDRAW_PAYMENT_REQUEST');
          return Observable.of();
        }).switchMap(() => {
          return forkJoin([
            this.paymentSummaryStore.reloadPaymentSummary(this.vendor.eventPartyId).take(1),
            this.paymentStore.loadPayments(this.onyxEvent.eventId).take(1),
            this.paymentStore.loadLatestFinalPayment(this.onyxEvent.eventId).take(1),
            this.paymentStore.loadLatestInitialPayments(this.onyxEvent.eventId).take(1),
            this.paymentStore.loadFinalPaymentStatusList(this.onyxEvent.eventId).take(1),
            this.paymentStore.getVendorCurrentPayment(this.vendor.eventPartyId).take(1),
            this.adjustmentStore.loadVendorsTotalPaymentsSummary(this.onyxEvent.eventId).take(1),
          ]);
        }).subscribe(() => {
          this.flashMessagesService.success('EVENT.COMMISSION.WITHDRAW_PAYMENT_REQUEST_SUCCESS');
        }));
    }
  }

  private computePaymentStatus(latestFinalPaymentStatus: PaymentStatus) {
    if (latestFinalPaymentStatus) {
      this.ui.showPaymentSummary = latestFinalPaymentStatus !== PaymentStatus.REQUEST_REJECTED
        && latestFinalPaymentStatus !== PaymentStatus.INVOICE_REJECTED;
      this.ui.showAddPayment = latestFinalPaymentStatus === PaymentStatus.REQUEST_REJECTED
        || latestFinalPaymentStatus === PaymentStatus.INVOICE_REJECTED;
      this.ui.showDownloadDropdown = true;
      this.isFinalPaymentAuthorized = true;
    } else {
      this.ui.showPaymentSummary = false;
      this.ui.showAddPayment = true;
      this.ui.showDownloadDropdown = false;
      this.isFinalPaymentAuthorized = false;
    }
  }

  private setupUi(): Observable<any> {
    return this.paymentStore.getVendorFinalPaymentStatusList(this.vendor.eventPartyId).flatMap((finalPaymentStatusList: Payment[]) => {
        return this.getPrepayment().map((prepayment: Payment) => {

        this.finalPaymentStatusList = finalPaymentStatusList;
        this.prepayment = prepayment;

        if (prepayment) {
          this.hasPrepayment = true;
        } else {
          this.hasPrepayment = false;
        }

        if ((this.finalPaymentStatusList && this.finalPaymentStatusList.length !== 0) || this.hasPrepayment) {
          this.ui.showPaymentRecord = true;
        } else {
          this.ui.showPaymentRecord = false;
        }
      });
    });
  }

  getPaymentStatus(status: PaymentStatus) {
    return PaymentStatusUtils.getAwePaymentStatus(status, this.authenticationStore.isAgencyUser());
  }

  toggleCard(event: Event) {
    event.preventDefault();
    this.onToggleCard.emit();
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/payment/vendor-commission-card/vendor-commission-card.component.ts